<template>
  <div class="wallet_card">
    <img class="card_glow" src="@/assets/home/wallet_card_glow.svg" alt="" />
    <img
      :class="['card_dollar', $store.state.isMirroring && 'mirror']"
      src="@/assets/home/wallet_card_dollar.png"
      alt=""
    />
    <div class="balance_info">
      <div class="card_title">
        {{ $t('home.balance') }}
        <img
          v-if="showBalance"
          class="visible_icon"
          src="@/assets/home/balance_visible_icon_light.svg"
          @click="showBalance = false"
          alt=""
        />
        <img
          v-else
          class="visible_icon hidden_icon"
          src="@/assets/home/balance_hidden_icon.png"
          @click="showBalance = true"
          alt=""
        />
      </div>
      <div class="balance_amount">
        {{ formattedBalance }}
      </div>
    </div>
    <div class="withdraw_btn" :class="{ disabled: !canCashOut }" @click="toWithdrawal">
      {{ $t('basicLevel.cashOut') }}
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { spendableCents } from '@/utils'
import { markWithdrawFlowStart, postReportLog } from '@/utils/log'

export default {
  name: 'HomeWalletCard',
  props: {
    hasWithdrawMethod: {
      type: Boolean,
      default: false
    },
    balance: {
      type: Number,
      default: undefined
    },
    balanceReady: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showBalance: true,
      cardExposed: false,
      exposeTimer: null
    }
  },
  computed: {
    isBalanceKnown() {
      return this.balance != null && this.balance !== '' && !Number.isNaN(Number(this.balance))
    },
    formattedBalance() {
      if (!this.showBalance) return '$*.**'
      if (!this.isBalanceKnown) return '--'
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'symbol'
      }).format(spendableCents(this.balance) / 100)
    },
    canCashOut() {
      return this.isBalanceKnown && spendableCents(this.balance) >= 1000
    },
    kycOk() {
      return !!(this.$store.state.userInfo && this.$store.state.userInfo.level > 0)
    },
    canWithdraw() {
      return this.hasWithdrawMethod && this.kycOk
    }
  },
  watch: {
    balance() {
      this.tryExposeCard()
    },
    balanceReady() {
      this.tryExposeCard()
    },
    hasWithdrawMethod() {
      this.tryExposeCard()
    }
  },
  mounted() {
    this.tryExposeCard()
    this.exposeTimer = setTimeout(() => this.tryExposeCard(true), 3000)
  },
  beforeDestroy() {
    this.clearExposeTimer()
  },
  methods: {
    clearExposeTimer() {
      if (this.exposeTimer) {
        clearTimeout(this.exposeTimer)
        this.exposeTimer = null
      }
    },
    cardLogExtra(extra) {
      return {
        requirement_met: this.canWithdraw ? '1' : '0',
        kyc_ok: this.kycOk ? '1' : '0',
        bind_ok: this.hasWithdrawMethod ? '1' : '0',
        ...(this.isBalanceKnown ? { balance_ok: this.canCashOut ? '1' : '0' } : {}),
        ...extra
      }
    },
    tryExposeCard(force) {
      if (this.cardExposed) return
      if (!this.balanceReady && force !== true) return
      this.cardExposed = true
      this.clearExposeTimer()
      postReportLog({
        log_id: '478',
        extra: this.cardLogExtra({
          step: '1'
        })
      })
    },
    toWithdrawal() {
      const level = (this.$store.state.userInfo && this.$store.state.userInfo.level) || 0
      if (!this.balanceReady || !this.isBalanceKnown) {
        postReportLog({
          log_id: '478',
          extra: this.cardLogExtra({
            step: '2',
            disabled: '1',
            reason: this.balanceReady ? 'balance_unknown' : 'balance_loading'
          })
        })
        if (!this.balanceReady) {
          Toast({
            message: this.$t('home.loading')
          })
        }
        return
      }
      postReportLog({
        log_id: '478',
        extra: this.cardLogExtra({
          step: '2',
          disabled: this.canCashOut ? '0' : '1',
          reason: this.canCashOut ? '' : 'balance_lt_10'
        })
      })
      if (!this.canCashOut) {
        Toast({
          message: this.$t('withdrawAccount.amountLimitToast2', [10])
        })
        return
      }
      markWithdrawFlowStart()
      if (level < 1) {
        this.$router.push('/basic-level')
        return
      }
      if (!this.hasWithdrawMethod) {
        this.$router.push({
          name: 'MyLinkedAccount',
          query: { from: 'BasicLevel' }
        })
        return
      }
      this.$router.push('/withdrawal')
    }
  }
}
</script>

<style scoped>
.wallet_card {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 0.32rem);
  min-height: 1.06rem;
  margin: 0 0.16rem;
  padding: 0.21rem 0.2rem;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 0.14rem;
  background: #08a864;
  border: 0.01rem solid #27a358;
}

.card_glow {
  position: absolute;
  z-index: 0;
  right: -0.89rem;
  top: -0.96rem;
  width: 3.59rem;
  height: 1.87rem;
  pointer-events: none;
  transform: rotate(11.34deg);
  -webkit-user-drag: none;
  user-select: none;
}

.card_dollar {
  position: absolute;
  z-index: 0;
  right: -0.18rem;
  top: -0.2rem;
  width: 1.04rem;
  height: 1.04rem;
  opacity: 0.15;
  pointer-events: none;
  object-fit: cover;
  -webkit-user-drag: none;
  user-select: none;
}

.card_dollar.mirror {
  right: auto;
  left: -0.18rem;
  transform: rotateY(180deg);
}

.balance_info {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 0.07rem;
}

.card_title {
  display: flex;
  align-items: center;
  color: #f3f5f7;
  font-family: 'PingFang SC';
  font-size: 0.16rem;
  font-style: normal;
  font-weight: 400;
  line-height: 0.24rem;
}

.visible_icon {
  width: 0.2rem;
  height: 0.2rem;
  margin-inline-start: 0.04rem;
  flex-shrink: 0;
}

.hidden_icon {
  filter: brightness(0) invert(1);
}

.balance_amount {
  color: #f3f5f7;
  font-family: 'PingFang SC';
  font-size: 0.3rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.32rem;
  white-space: nowrap;
}

.withdraw_btn {
  position: relative;
  z-index: 2;
  display: flex;
  height: 0.36rem;
  padding: 0 0.16rem;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #045d34;
  font-family: 'PingFang SC';
  font-size: 0.12rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.2rem;
  border-radius: 0.24rem;
  background-color: #ffffff;
}

.withdraw_btn.disabled {
  background-color: rgba(255, 255, 255, 0.4);
  color: rgba(4, 93, 52, 0.35);
}
</style>



// WEBPACK FOOTER //
// src/components/Home/HomeWalletCard.vue